import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@monbits/ui";
import { BookOpen } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import DrawerForm from "src/components/drawer-form/drawer-form";
import WebhookSettingsForm from "src/components/webhook-settings-form/webhook-settings-form";
import useSharedMerchant from "src/hooks/use-merchant";
import useActiveMerchantId from "src/hooks/use-merchant-id";
import merchantProvider from "src/providers/merchant-provider";
import type { WebhookSettings } from "src/types";
import { errorMessage } from "src/utils/error-message";

interface Props {
	openPopupFunc: (title: string, desc: string) => void;
}

const WebhooksSection: React.FC<Props> = ({ openPopupFunc }) => {
	const { merchantId } = useActiveMerchantId();
	const { merchant, getMerchant } = useSharedMerchant();
	const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	React.useEffect(() => {
		if (merchantId) {
			getMerchant(merchantId);
		}
	}, [merchantId, getMerchant]);

	const updateWebhookSettings = async (settings: WebhookSettings) => {
		if (!merchantId) return;
		try {
			setIsSubmitting(true);
			await merchantProvider.updateMerchantWebhookSettings(
				merchantId,
				settings,
			);
			setIsSettingsOpen(false);
			openPopupFunc(
				"Webhook settings saved",
				"We'll deliver new events to your endpoint.",
			);
			await getMerchant(merchantId);
		} catch (error) {
			toast.error(errorMessage(error, "Failed to save webhook settings"));
		} finally {
			setIsSubmitting(false);
		}
	};

	const endpointUrl = merchant?.webhookSettings?.url;

	return (
		<>
			<Card>
				<CardHeader className="pb-2">
					<div className="flex items-start justify-between gap-3">
						<div className="space-y-1">
							<CardTitle className="text-base font-medium">Webhooks</CardTitle>
							<CardDescription className="text-xs">
								Receive real-time payment notifications at your endpoint.
							</CardDescription>
						</div>
						<Button
							variant="outline"
							size="sm"
							className="flex-shrink-0"
							onClick={() => setIsSettingsOpen(true)}
						>
							Manage settings
						</Button>
					</div>
				</CardHeader>
				<CardContent className="space-y-5">
					<div>
						<p className="mb-2 text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
							Endpoint
						</p>
						{endpointUrl ? (
							<div className="rounded-lg border border-border bg-muted/60 p-3">
								<code className="break-all font-mono text-sm">
									{endpointUrl}
								</code>
							</div>
						) : (
							<p className="text-sm text-muted-foreground">
								Not configured yet.
							</p>
						)}
					</div>
					<a
						href="https://api-docs.pay.monbits.com/#description/webhook-notifications-"
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center text-sm font-medium text-primary hover:underline"
					>
						<BookOpen aria-hidden="true" className="mr-2 h-3.5 w-3.5" />
						Webhook documentation
					</a>
				</CardContent>
			</Card>

			<DrawerForm
				title="Webhook settings"
				description="We'll send signed payment, order, and withdrawal events here."
				isOpen={isSettingsOpen}
				onClose={() => setIsSettingsOpen(false)}
				isSubmitting={isSubmitting}
				submitText="Save settings"
				width={512}
			>
				<WebhookSettingsForm
					onFinish={updateWebhookSettings}
					webhookSettings={merchant?.webhookSettings}
					isFormSubmitting={isSubmitting}
				/>
			</DrawerForm>
		</>
	);
};

export default WebhooksSection;
